/**
 * Scene Editor Handler Callbacks
 * Feature: V2 3-column Reel Scene Editor
 *
 * Extracted from SceneEditorContainer to keep file under 300 lines.
 * Contains all handler callbacks for the scene editor page.
 */

import { useCallback, useRef, useState } from 'react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IReelScene, ISceneEditPayload } from '@/@types/reelScenes';
import type { FrameMode } from '@/@types/sceneEditorV2';
import type { ISceneVoiceover, IVoiceoverGenerationResult } from '@/@types/voices';

// ============================================================================
// Types
// ============================================================================

interface ISceneEditorHandlersDeps {
  /** saveScene from useSceneEditor */
  saveScene: (sceneId: Id<'reelScenes'>, changes: ISceneEditPayload) => Promise<void>;
  /** deleteScene from useSceneEditor */
  deleteScene: (sceneId: Id<'reelScenes'>) => Promise<void>;
  /** voiceoversMap from useVoiceoverGeneration */
  voiceoversMap: Map<string, ISceneVoiceover>;
  /** defaultVoiceId from useReelVoiceAssignment */
  defaultVoiceId: Id<'clonedVoices'> | null;
  /** generateSingle from useVoiceoverGeneration */
  generateSingle: (
    sceneId: Id<'reelScenes'>,
    voiceId: Id<'clonedVoices'>,
    text: string
  ) => Promise<IVoiceoverGenerationResult>;
  /** regenerate from useVoiceoverGeneration */
  regenerateVoiceover: (
    sceneId: Id<'reelScenes'>,
    voiceId?: Id<'clonedVoices'>
  ) => Promise<unknown>;
  /** Sorted scenes for lookup */
  sortedScenes: IReelScene[];
  /** Selected scene */
  selectedScene: IReelScene | null;
  /** regenerateImage from useSingleSceneImage */
  regenerateImage: (
    sceneId: Id<'reelScenes'>,
    frame: 'first' | 'last',
    customPrompt?: string
  ) => Promise<void>;
  /** renderScene from useRenderScene */
  renderScene: (
    sceneId: Id<'reelScenes'>,
    voiceId: Id<'clonedVoices'>,
    frameMode: FrameMode
  ) => Promise<void>;
  /** frameMode local state */
  frameMode: FrameMode;
  /** setFrameMode state setter */
  setFrameMode: React.Dispatch<React.SetStateAction<FrameMode>>;
  /** setIsTimelinePlaying state setter */
  setIsTimelinePlaying: React.Dispatch<React.SetStateAction<boolean>>;
  /** setTimelineCurrentTime state setter */
  setTimelineCurrentTime: React.Dispatch<React.SetStateAction<number>>;
}

interface ISceneEditorHandlersReturn {
  handleUpdateVoiceoverText: (sceneId: Id<'reelScenes'>, text: string) => Promise<void>;
  handleDeleteScene: (sceneId: Id<'reelScenes'>) => Promise<void>;
  handlePlayAudio: (sceneId: Id<'reelScenes'>) => Promise<void>;
  handleStopAudio: () => void;
  handleRegenerateAudio: (sceneId: Id<'reelScenes'>) => Promise<void>;
  handleRenderScene: () => Promise<void>;
  handleRegenerateImage: (frame: 'first' | 'last', prompt: string) => Promise<void>;
  handleToggleFrameMode: () => void;
  handleUpdateDeliveryStyle: (sceneId: Id<'reelScenes'>, prompt: string) => Promise<void>;
  handleAddScene: () => void;
  handleTimelinePlayPause: () => void;
  handleTimelineSeek: (time: number) => void;
  audioPlayingSceneId: Id<'reelScenes'> | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useSceneEditorHandlers(deps: ISceneEditorHandlersDeps): ISceneEditorHandlersReturn {
  const {
    saveScene,
    deleteScene,
    voiceoversMap,
    defaultVoiceId,
    generateSingle,
    regenerateVoiceover,
    sortedScenes,
    selectedScene,
    regenerateImage,
    renderScene,
    frameMode,
    setFrameMode,
    setIsTimelinePlaying,
    setTimelineCurrentTime,
  } = deps;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlayingSceneId, setAudioPlayingSceneId] = useState<Id<'reelScenes'> | null>(null);

  const stopCurrentAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setAudioPlayingSceneId(null);
  }, []);

  const playAudioUrl = useCallback(
    (url: string, sceneId: Id<'reelScenes'>) => {
      stopCurrentAudio();
      const audio = new Audio(url);
      audioRef.current = audio;
      setAudioPlayingSceneId(sceneId);
      audio.addEventListener('ended', () => {
        audioRef.current = null;
        setAudioPlayingSceneId(null);
      });
      audio.play();
    },
    [stopCurrentAudio]
  );

  const handleUpdateVoiceoverText = useCallback(
    async (sceneId: Id<'reelScenes'>, text: string) => {
      await saveScene(sceneId, { editedVoiceoverText: text });
    },
    [saveScene]
  );

  const handleDeleteScene = useCallback(
    async (sceneId: Id<'reelScenes'>) => {
      await deleteScene(sceneId);
    },
    [deleteScene]
  );

  const handlePlayAudio = useCallback(
    async (sceneId: Id<'reelScenes'>) => {
      // If this scene is already playing, stop it
      if (audioPlayingSceneId === sceneId) {
        stopCurrentAudio();
        return;
      }

      const voiceover = voiceoversMap?.get(sceneId.toString());
      const scene = sortedScenes.find(s => s._id === sceneId);
      const effectiveText = scene ? (scene.editedVoiceoverText ?? scene.voiceoverText ?? '') : '';

      // Check if audio is stale (text changed since last generation)
      const isStale = voiceover?.audioUrl && voiceover.voiceoverText !== effectiveText;

      if (voiceover?.audioUrl && !isStale) {
        playAudioUrl(voiceover.audioUrl, sceneId);
      } else if (defaultVoiceId && scene) {
        // Generate new audio (either no audio exists, or audio is stale)
        const result = isStale
          ? ((await regenerateVoiceover(sceneId, defaultVoiceId)) as IVoiceoverGenerationResult)
          : await generateSingle(sceneId, defaultVoiceId, effectiveText);
        if (result.success && result.audioUrl) {
          playAudioUrl(result.audioUrl, sceneId);
        }
      }
    },
    [
      voiceoversMap,
      defaultVoiceId,
      generateSingle,
      regenerateVoiceover,
      sortedScenes,
      audioPlayingSceneId,
      stopCurrentAudio,
      playAudioUrl,
    ]
  );

  const handleStopAudio = useCallback(() => {
    stopCurrentAudio();
  }, [stopCurrentAudio]);

  const handleRegenerateAudio = useCallback(
    async (sceneId: Id<'reelScenes'>) => {
      if (defaultVoiceId) {
        await regenerateVoiceover(sceneId, defaultVoiceId);
      }
    },
    [regenerateVoiceover, defaultVoiceId]
  );

  const handleRenderScene = useCallback(async () => {
    if (!selectedScene || !defaultVoiceId) return;
    await renderScene(selectedScene._id, defaultVoiceId, frameMode);
  }, [selectedScene, defaultVoiceId, renderScene, frameMode]);

  const handleRegenerateImage = useCallback(
    async (frame: 'first' | 'last', prompt: string) => {
      if (!selectedScene) return;
      await regenerateImage(selectedScene._id, frame, prompt);
    },
    [selectedScene, regenerateImage]
  );

  const handleToggleFrameMode = useCallback(() => {
    setFrameMode(prev => (prev === 'start-only' ? 'start-end' : 'start-only'));
  }, [setFrameMode]);

  const handleUpdateDeliveryStyle = useCallback(
    async (sceneId: Id<'reelScenes'>, prompt: string) => {
      await saveScene(sceneId, { videoPrompt: prompt });
    },
    [saveScene]
  );

  const handleAddScene = useCallback(() => {
    // TODO: wire to addScene mutation when available
  }, []);

  const handleTimelinePlayPause = useCallback(() => {
    setIsTimelinePlaying(prev => !prev);
  }, [setIsTimelinePlaying]);

  const handleTimelineSeek = useCallback(
    (time: number) => {
      setTimelineCurrentTime(time);
    },
    [setTimelineCurrentTime]
  );

  return {
    handleUpdateVoiceoverText,
    handleDeleteScene,
    handlePlayAudio,
    handleStopAudio,
    handleRegenerateAudio,
    handleRenderScene,
    handleRegenerateImage,
    handleToggleFrameMode,
    handleUpdateDeliveryStyle,
    handleAddScene,
    handleTimelinePlayPause,
    handleTimelineSeek,
    audioPlayingSceneId,
  };
}
